import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useApi, useLoad } from "@/lib/api";
import { Panel, Stat, Table, day } from "@/pages/console/Kit";

function Ops() {
  const o = useLoad("/admin/ops").data;
  if (!o) return null;
  return (
    <div className="space-y-4" data-testid="console-ops">
      <div className="grid gap-4 sm:grid-cols-3">
        <Stat label="Emails (7 days)" value={o.email_7d.total} sub={`${o.email_7d.delivery_pct}% sent · ${o.email_7d.errors} errors`} />
        <Stat label="Job errors (7 days)" value={o.errors_7d} />
        <Stat label="Backups" value="Daily" sub={o.backups} />
      </div>
      <Panel title="Scheduled jobs"><Table rows={o.cron} cols={[["path", "Job"], ["last", "Last run", (c) => day(c.last)]]} /></Panel>
    </div>
  );
}

function Rights() {
  const api = useApi();
  const r = useLoad("/admin/rights");
  const [f, setF] = useState({ person: "", kind: "access", notes: "" });
  const add = async () => { try { await api("post", "/admin/rights", f); setF({ person: "", kind: "access", notes: "" }); r.reload(); } catch (e) { toast.error(e.message); } };
  return (
    <Panel title="Data rights requests (30-day deadline)" testid="console-rights">
      <div className="mb-3 flex flex-wrap gap-2">
        <Input className="w-56" placeholder="Person" value={f.person} onChange={(e) => setF({ ...f, person: e.target.value })} data-testid="rights-person-input" />
        <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className="h-10 rounded-md border bg-white px-2 text-sm">{["access", "correction", "deletion", "objection", "portability", "restriction"].map((k) => <option key={k}>{k}</option>)}</select>
        <Input className="flex-1" placeholder="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
        <Button onClick={add} disabled={f.person.length < 2} data-testid="rights-add-button">Log request</Button>
      </div>
      <Table rows={r.data || []} cols={[["person", "Person"], ["kind", "Type"], ["status", "Status"], ["due", "Due", (x) => day(x.due)],
        ["x", "", (x) => x.status === "open" && <Button size="sm" variant="outline" onClick={() => api("post", `/admin/rights/${x.id}/close`).then(r.reload)}>Close</Button>]]} />
    </Panel>
  );
}

function Notices() {
  const api = useApi();
  const n = useLoad("/admin/notices");
  const [f, setF] = useState({ kind: "subprocessor", title: "", text: "", effective: "" });
  const send = async () => { try { const r = await api("post", "/admin/notices", f); toast.success(`Sent to ${r.recipients} owners`); n.reload(); } catch (e) { toast.error(e.message); } };
  return (
    <Panel title="Change notices to customers" testid="console-notices">
      <div className="mb-3 grid gap-2 sm:grid-cols-[160px_1fr_170px]">
        <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className="h-10 rounded-md border bg-white px-2 text-sm"><option value="subprocessor">Sub-processor</option><option value="legal">Legal document</option></select>
        <Input placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
        <Input type="date" value={f.effective} onChange={(e) => setF({ ...f, effective: e.target.value })} />
        <Textarea className="sm:col-span-3" rows={3} placeholder="What is changing (at least 30 days' notice)" value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} />
      </div>
      <Button onClick={send} disabled={!f.title || f.text.length < 10 || !f.effective} data-testid="notice-send-button">Send notice</Button>
      <div className="mt-4"><Table rows={n.data || []} cols={[["title", "Title"], ["effective", "Effective"], ["recipients", "Recipients"], ["objections", "Objections", (x) => x.objections.length]]} /></div>
    </Panel>
  );
}

function Audit() {
  const [q, setQ] = useState("");
  const a = useLoad(`/admin/audit?q=${encodeURIComponent(q)}`);
  return (
    <Panel title="Audit log" testid="console-audit" right={<Input className="w-56" placeholder="Search action or person" value={q} onChange={(e) => setQ(e.target.value)} data-testid="audit-search" />}>
      <div className="max-h-96 overflow-y-auto"><Table rows={a.data || []} cols={[["at", "When", (x) => new Date(x.at).toLocaleString("en-GB")], ["action", "Action"], ["by", "By"], ["org_id", "Org", (x) => (x.org_id || "").slice(0, 8)]]} /></div>
    </Panel>
  );
}

export default function Compliance() {
  return <div className="space-y-6"><Ops /><Rights /><Notices /><Audit /></div>;
}
